import { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  LayoutGrid,
  X,
  Search,
  ArrowRight,
  Building,
  Loader2,
  AlertCircle,
  MapPin,
  Bed,
  Maximize,
  Star,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useToast } from "../components/ui/toast";
import { PropertyCore, PropertyTemplate } from "../types/property";
import {
  withUnifiedPurchasePrice,
  normalizeFacilities,
  normalizeRoomTypes,
} from "../lib/deals/normalizers";
import { asStr, safeLocation } from "../lib/strings";
import { getDataSource } from "../lib/datasource";
import { useSlimTemplateProperties } from "../hooks/useProperties";
import { DEFAULT_KPIS } from "@/lib/kpis/defaults";
import { SlimPropertyTemplate } from "../lib/services/propertyService";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";

interface CreateDealFormState {
  property: PropertyCore;
  dealName: string;
  selectedTemplate: PropertyTemplate | null;
}

const defaultProperty: PropertyCore = {
  name: "",
  location: "",
  address: "",
  photoUrl: "",
  propertyType: "Boutique",
  starRating: 4,
  currency: "EUR",
  gfaSqm: 0,
  purchasePrice: 0,
  facilities: [],
  roomTypes: [],
  totalRooms: 0,
};

const isUuid = (v?: string | null) =>
  !!v &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    v,
  );

function templateLocation(t?: {
  city?: string;
  country?: string;
  address?: string;
}) {
  return safeLocation(t?.city, t?.country) || asStr(t?.address, "—");
}

const facilityLabels: Record<string, string> = {
  parking: "Parking",
  restaurant: "Restaurant",
  bar: "Bar",
  pool: "Pool",
  spa: "Spa",
  gym: "Gym",
  conference: "Conference",
  roomservice: "Room Service",
  rooftop: "Rooftop",
  yoga: "Yoga",
  wifi: "Wi-Fi",
  internet: "Internet",
  "fitness-center": "Fitness Center",
  "business-center": "Business Center",
};

const formatPrice = (price: number, currency: string) => {
  const symbol = currency === "EUR" ? "€" : "$";

  if (price >= 1000000) {
    const value = price / 1000000;
    return `${symbol}${value % 1 === 0 ? value.toFixed(0) : value.toFixed(1)}M`;
  }

  if (price >= 1000) {
    const value = price / 1000;
    return `${symbol}${value % 1 === 0 ? value.toFixed(0) : value.toFixed(1)}K`;
  }

  return `${symbol}${price.toLocaleString()}`;
};

export default function CreateDealPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [form, setForm] = useState<CreateDealFormState>({
    property: defaultProperty,
    dealName: "",
    selectedTemplate: null,
  });
  const [isCreating, setIsCreating] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [loadingFullTemplate, setLoadingFullTemplate] = useState<string | null>(
    null,
  );
  const [selectedDetailTemplate, setSelectedDetailTemplate] =
    useState<PropertyTemplate | null>(null);
  const [detailImageIndex, setDetailImageIndex] = useState(0);

  const { templates: slimTemplates, loading } = useSlimTemplateProperties();

  const filteredTemplates = useMemo(() => {
    if (!searchTerm) return slimTemplates;
    const lower = searchTerm.toLowerCase();
    return slimTemplates.filter(
      (t) =>
        t.dealName?.toLowerCase().includes(lower) ||
        t.city?.toLowerCase().includes(lower) ||
        t.country?.toLowerCase().includes(lower),
    );
  }, [slimTemplates, searchTerm]);

  const selectedRooms = form.selectedTemplate?.total_rooms || 0;

  const selectedLocation = templateLocation(form.selectedTemplate || undefined);

  useEffect(() => {
    const template = (location.state as any)?.templateProperty;
    if (template) {
      handleTemplateSelect(template);
    }
  }, [location.state]);

  const handleTemplateSelect = async (template: SlimPropertyTemplate) => {
    try {
      setLoadingFullTemplate(template.id);
      processFullTemplate(template);
    } catch (error) {
      console.error("Failed to load template:", error);
      toast.error("Failed to load template details");
    } finally {
      setLoadingFullTemplate(null);
    }
  };

  const processFullTemplate = (template: SlimPropertyTemplate) => {
    const unified = withUnifiedPurchasePrice({ ...template });

    // Get room types from kpis.roomMix if available, otherwise create empty array
    const roomTypes =
      template.kpis?.roomMix?.map((r: any) => ({
        id: r.id || crypto.randomUUID(),
        name: asStr(r.name, "Room"),
        count: r.rooms || 0,
        baseAdr: r.adrWeight,
        baseOcc: undefined,
      })) || [];

    const normalizedRoomTypes = normalizeRoomTypes(roomTypes);

    const rawNormalized = normalizeFacilities(template.facilities || []);

    const normalizedFacilities = rawNormalized.map((f: any) =>
      typeof f === "object" && f !== null && f.label ? f.label : String(f),
    );

    // Get total rooms from either rooms_total or total_rooms
    const totalRooms =
      template.rooms_total || (template as any).total_rooms || 0;

    const normalizedTemplate: PropertyTemplate = {
      ...unified,
      rooms: normalizedRoomTypes,
      facilities: normalizedFacilities,
      kpis: template.kpis,
      total_rooms: totalRooms,
    };

    setForm((prev) => ({
      ...prev,
      selectedTemplate: normalizedTemplate,
      dealName: `${asStr(template.dealName, "Property")} Analysis`,
      property: {
        name: asStr(template.dealName, "Untitled Property"),
        location: templateLocation(template),
        address: asStr(template.fullAddress, ""),
        photoUrl: asStr(template.photoUrl, ""),
        propertyType:
          (template.propertyType as PropertyCore["propertyType"]) || "Boutique",
        starRating: template.stars || 4,
        currency: template.currency || "EUR",
        gfaSqm: template.gfaSqm || 0,
        purchasePrice: template.purchasePrice || 0,
        facilities: normalizedFacilities,
        roomTypes: roomTypes,
        totalRooms: totalRooms,
      },
    }));

    if (window.innerWidth < 1024) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };
  const handleClearTemplate = () => {
    setForm((prev) => ({
      ...prev,
      selectedTemplate: null,
      dealName: "",
      property: defaultProperty,
    }));
  };

  const handleCreateDeal = async () => {
    if (!form.dealName.trim()) {
      toast.error("Please enter a deal name");
      return;
    }

    setIsCreating(true);

    try {
      const optimisticId = crypto.randomUUID();
      const ds = getDataSource();

      let templateKpis: any = {};
      if (form.selectedTemplate?.id) {
        try {
          const fetchedKpis = await ds.getPropertyTemplateKpis(
            form.selectedTemplate.id,
          );
          templateKpis = fetchedKpis || {};
        } catch (error) {
          console.error("Failed to load template KPIs:", error);
        }
      }
      const sourcePayroll = templateKpis?.payroll || DEFAULT_KPIS.payroll;

      const finalKpis = {
        ...DEFAULT_KPIS,
        ...templateKpis,

        dealName: form.dealName.trim(),

        payroll: sourcePayroll,

        rooms: {
          ...(templateKpis?.rooms || DEFAULT_KPIS.rooms || {}),
          total: selectedRooms || templateKpis?.rooms?.total || 0,
        },

        location: selectedLocation || templateKpis?.location || "",
      };

      const candidatePropertyId =
        (form.selectedTemplate as any)?.property_id ||
        form.selectedTemplate?.id;
      const property_id = isUuid(candidatePropertyId)
        ? candidatePropertyId
        : null;

      const savedProject = await ds.saveProject({
        id: optimisticId,
        name: form.dealName.trim(),
        property_id,
        stage: "underwriting",
        currency: finalKpis.currency || "EUR",
        kpis: finalKpis,
      });

      toast.success("Deal created successfully!");
      navigate(`/underwriting/${savedProject?.id || optimisticId}`, {
        replace: true,
      });
    } catch (error) {
      console.error("Create error:", error);
      toast.error("Error creating deal. Check console for details.");
    } finally {
      setIsCreating(false);
    }
  };

  const handleCardClick = (templateId: string) => {
    const template = slimTemplates.find((t) => t.id === templateId);
    if (template) {
      setSelectedDetailTemplate(template as PropertyTemplate);
      setDetailImageIndex(0);
    }
  };

  const handleUseAsTemplate = () => {
    if (selectedDetailTemplate) {
      handleTemplateSelect(selectedDetailTemplate as SlimPropertyTemplate);
      setSelectedDetailTemplate(null);
    }
  };

  return (
    <div className="min-h-screen ">
      <div className="container mx-auto px-4 lg:px-8 py-8 max-w-[1600px]">
        <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
              New Analysis
            </h1>
            <p className="text-slate-500 mt-2 text-lg max-w-2xl">
              Create a new financial model from scratch or use a verified
              template.
            </p>
          </div>
          <Button
            variant="ghost"
            onClick={() => navigate("/underwriting")}
            className="text-slate-500 hover:text-slate-900 self-start md:self-end"
          >
            Cancel
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-4 lg:sticky lg:top-6 z-10 order-2 lg:order-1">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-slate-900 animate-pulse" />
                <h2 className="font-semibold text-slate-900">Configuration</h2>
              </div>

              <div className="p-6 space-y-6">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-700">
                    Project Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    autoFocus
                    type="text"
                    value={form.dealName}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, dealName: e.target.value }))
                    }
                    className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900 transition-all text-slate-900 placeholder:text-slate-400 shadow-sm"
                    placeholder="e.g. Hotel Metropole Acquisition"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-700">
                    Base Template
                  </label>

                  {form.selectedTemplate ? (
                    <div className="relative bg-slate-50 rounded-xl border border-slate-200 p-4 transition-all hover:bg-slate-100 group">
                      <div className="flex justify-between items-start mb-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-slate-200 text-slate-800 uppercase tracking-wide">
                          Selected
                        </span>
                        <button
                          onClick={handleClearTemplate}
                          disabled={
                            loadingFullTemplate === form.selectedTemplate.id
                          }
                          className="text-slate-400 hover:text-red-500 transition-colors p-1 rounded-full hover:bg-white disabled:opacity-50"
                          title="Remove template"
                        >
                          {loadingFullTemplate === form.selectedTemplate.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <X className="w-4 h-4" />
                          )}
                        </button>
                      </div>

                      <div className="flex gap-3 items-center">
                        <div className="w-12 h-12 rounded-lg bg-slate-200 overflow-hidden shrink-0 border border-slate-200">
                          {form.selectedTemplate.photoUrl ? (
                            <img
                              src={form.selectedTemplate.photoUrl}
                              className="w-full h-full object-cover"
                              alt=""
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-slate-400 bg-slate-100">
                              <Building className="w-5 h-5" />
                            </div>
                          )}
                        </div>
                        <div>
                          <div className="font-semibold text-slate-900 line-clamp-1">
                            {asStr(
                              form.selectedTemplate.dealName,
                              "Untitled Property",
                            )}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2">
                            <span>
                              {form.selectedTemplate?.total_rooms || 0} rooms
                            </span>
                            <span className="w-1 h-1 rounded-full bg-slate-300"></span>
                            <span className="truncate max-w-[120px]">
                              {selectedLocation}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-slate-50 rounded-xl border border-slate-200 border-dashed p-6 text-center transition-colors hover:bg-slate-100/80">
                      <div className="mx-auto w-10 h-10 bg-white border border-slate-200 rounded-full flex items-center justify-center mb-2 shadow-sm">
                        <LayoutGrid className="w-5 h-5 text-slate-400" />
                      </div>
                      <p className="text-sm text-slate-600 font-medium">
                        From Scratch
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        Starting with blank assumptions. Select a template on
                        the right to pre-fill data.
                      </p>
                    </div>
                  )}
                </div>

                <div className="pt-2">
                  <Button
                    onClick={handleCreateDeal}
                    disabled={
                      !form.dealName.trim() ||
                      isCreating ||
                      loadingFullTemplate !== null
                    }
                    className="w-full h-12 text-base font-semibold rounded-xl flex items-center justify-center gap-2 transition-all hover:bg-primary-blue/90 active:scale-[0.98] bg-primary-blue text-white shadow-lg hover:shadow-xl"
                  >
                    {isCreating ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>Creating Project...</span>
                      </>
                    ) : (
                      <>
                        <span>Start Analysis</span>
                        <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-8 order-1 lg:order-2 space-y-6">
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-slate-400 group-focus-within:text-slate-900 transition-colors" />
              </div>
              <input
                type="text"
                placeholder="Search templates by name, city or country..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-11 pr-4 py-3 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900 shadow-sm text-slate-900 placeholder:text-slate-400 transition-all"
              />
            </div>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className="bg-white rounded-2xl border border-slate-200 p-5 animate-pulse"
                  >
                    <div className="h-40 bg-slate-200 rounded-xl mb-4"></div>
                    <div className="h-4 bg-slate-200 rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-slate-200 rounded w-1/2"></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {filteredTemplates.map((property) => (
                  <div
                    key={property.id}
                    onClick={() => handleCardClick(property.id)}
                    className="bg-white border border-slate-200 rounded-2xl overflow-hidden cursor-pointer hover:shadow-xl transition-shadow group"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={property.photoUrl}
                        alt={property.dealName}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      {property.featured && (
                        <Badge className="absolute top-3 left-3 bg-amber-400/90 text-amber-950 hover:bg-amber-500 border-none backdrop-blur-sm shadow-sm">
                          Featured
                        </Badge>
                      )}
                      <div className="absolute bottom-3 right-3 bg-white/95 backdrop-blur-sm px-2 py-1 rounded text-sm font-semibold">
                        {formatPrice(
                          property.purchasePrice || 0,
                          property.currency || "EUR",
                        )}
                      </div>
                    </div>

                    <div className="p-4">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="font-semibold text-lg text-slate-900">
                          {property.dealName}
                        </h3>
                        {property.stars && (
                          <div className="flex items-center gap-1 text-amber-500">
                            <span className="text-sm font-medium">
                              {property.stars}
                            </span>
                            <Star className="w-4 h-4 fill-current" />
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-1 text-slate-500 text-sm mb-3">
                        <MapPin className="w-3.5 h-3.5" />
                        {templateLocation(property)}
                      </div>

                      <div className="flex items-center gap-4 text-sm text-slate-500 mb-3">
                        <div className="flex items-center gap-1">
                          <Bed className="w-4 h-4" />
                          <span>{property.total_rooms || 0} Rooms</span>
                        </div>
                        {property.gfaSqm && (
                          <div className="flex items-center gap-1">
                            <Maximize className="w-4 h-4" />
                            <span>{property.gfaSqm.toLocaleString()} m²</span>
                          </div>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {property.facilities?.slice(0, 4).map((facility) => (
                          <span
                            key={facility}
                            className="text-xs uppercase tracking-wide text-slate-600"
                          >
                            {facilityLabels[facility] || facility}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}

                {filteredTemplates.length === 0 && (
                  <div className="col-span-full py-16 text-center bg-white rounded-2xl border border-slate-200 border-dashed">
                    <div className="mx-auto w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center mb-3">
                      <AlertCircle className="w-6 h-6 text-slate-300" />
                    </div>
                    <h3 className="text-slate-900 font-medium">
                      No templates found
                    </h3>
                    <p className="text-slate-500 text-sm mt-1">
                      Try adjusting your search terms
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <Dialog
        open={!!selectedDetailTemplate}
        onOpenChange={() => setSelectedDetailTemplate(null)}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] p-0 overflow-y-auto">
          {selectedDetailTemplate && (
            <>
              <div className="relative h-72">
                <img
                  src={selectedDetailTemplate.photoUrl}
                  alt={selectedDetailTemplate.dealName}
                  className="w-full h-full object-cover"
                />
                {selectedDetailTemplate.featured && (
                  <Badge className="absolute top-4 left-4 bg-amber-400/90 text-amber-950 hover:bg-amber-500 border-none backdrop-blur-sm shadow-sm">
                    Featured
                  </Badge>
                )}
                <div className="absolute bottom-4 right-4 bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-lg text-lg font-bold">
                  {formatPrice(
                    selectedDetailTemplate.purchasePrice || 0,
                    selectedDetailTemplate.currency || "EUR",
                  )}
                </div>
              </div>

              <div className="p-6 pt-4">
                <DialogHeader>
                  <div className="flex items-center justify-between">
                    <DialogTitle className="text-2xl text-slate-900">
                      {selectedDetailTemplate.dealName}
                    </DialogTitle>
                    {selectedDetailTemplate.stars && (
                      <div className="flex items-center gap-1 text-amber-500">
                        <span className="text-lg font-medium">
                          {selectedDetailTemplate.stars}
                        </span>
                        <Star className="w-5 h-5 fill-current" />
                      </div>
                    )}
                  </div>
                </DialogHeader>

                <div className="flex items-center gap-1 text-slate-500 mb-4">
                  <MapPin className="w-4 h-4" />
                  {templateLocation(selectedDetailTemplate)}
                </div>

                <div className="grid grid-cols-4 gap-4 mb-6">
                  <div className="bg-slate-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-slate-900">
                      {selectedDetailTemplate.total_rooms || 0}
                    </p>
                    <p className="text-xs text-slate-500">Rooms</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-slate-900">
                      {selectedDetailTemplate.gfaSqm?.toLocaleString() || "—"}
                    </p>
                    <p className="text-xs text-slate-500">GFA m²</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-slate-900">
                      {selectedDetailTemplate.stars || "—"}
                    </p>
                    <p className="text-xs text-slate-500">Star Rating</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-slate-900 capitalize">
                      {(selectedDetailTemplate.propertyType || "").replace(
                        "-",
                        " ",
                      )}
                    </p>
                    <p className="text-xs text-slate-500">Type</p>
                  </div>
                </div>

                {selectedDetailTemplate.facilities &&
                  selectedDetailTemplate.facilities.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-6">
                      {selectedDetailTemplate.facilities.map((facility) => (
                        <Badge key={facility} variant="secondary">
                          {facilityLabels[facility] || facility}
                        </Badge>
                      ))}
                    </div>
                  )}

                <div className="mb-6">
                  <h4 className="font-semibold mb-2 text-slate-900">
                    Property Description
                  </h4>
                  <p className="text-slate-600">
                    {selectedDetailTemplate.description ||
                      "No description available."}
                  </p>
                </div>

                <div className="mb-6">
                  <h4 className="font-semibold mb-2 text-slate-900">
                    Investment Thesis
                  </h4>
                  <p className="text-slate-600">
                    {selectedDetailTemplate.investment_thesis ||
                      "No description available."}
                  </p>
                </div>

                <div className="flex gap-3">
                  <Button
                    className="flex-1 bg-primary-blue text-white"
                    onClick={handleUseAsTemplate}
                  >
                    Use as Template
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setSelectedDetailTemplate(null)}
                  >
                    Close
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
